import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLines } from "@/components/Reveal";

const EASE = [0.16, 1, 0.3, 1];

const Badge = () => (
  <div className="spin-slow absolute -left-14 top-[18%] z-20 hidden h-28 w-28 md:block" aria-hidden="true">
    <svg viewBox="0 0 100 100" className="h-full w-full">
      <defs><path id="c" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <circle cx="50" cy="50" r="49" fill="#121212" />
      <text fill="#FAF8F5" fontSize="9" fontFamily="Space Mono" letterSpacing="2.1">
        <textPath href="#c">GOENCHO ESSENTIALS · WORN DAILY · </textPath>
      </text>
      <rect x="45" y="45" width="10" height="10" fill="#E53935" />
    </svg>
  </div>
);

const HeroImage = () => {
  const frame = useRef(null);
  const { scrollYProgress } = useScroll({ target: frame, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 110, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 110, damping: 16 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    mx.set(x - 0.5);
    my.set(y - 0.5);
    e.currentTarget.style.setProperty("--sx", `${x * 100}%`);
    e.currentTarget.style.setProperty("--sy", `${y * 100}%`);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <div className="relative [perspective:1400px]" ref={frame}>
      <Badge />
      <motion.div
        data-testid="hero-image-frame"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
        transition={{ duration: 1.5, delay: 0.35, ease: EASE }}
        className="group relative aspect-[4/5] w-full overflow-hidden bg-sand [transform-style:preserve-3d]"
      >
        <motion.img
          src="/img/hood-grey.jpg"
          alt="goencho — heavyweight hoodie"
          style={{ y: imgY }}
          initial={{ scale: 1.25 }}
          animate={{ scale: 1.08 }}
          transition={{ duration: 2.2, delay: 0.35, ease: EASE }}
          className="absolute inset-0 h-full w-full object-cover grayscale-[15%]"
        />
        <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 [background:radial-gradient(circle_at_var(--sx,50%)_var(--sy,50%),rgba(250,248,245,0.28),transparent_38%)]" />
        <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-5 text-bone">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em]">Look 01 / Midnight</span>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em]">AW26</span>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const sec = useRef(null);
  const { scrollYProgress } = useScroll({ target: sec, offset: ["start start", "end start"] });
  const wordX = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section ref={sec} data-testid="hero-section" className="relative overflow-hidden pt-24 md:pt-28">
      <div className="mx-auto grid max-w-[1600px] gap-10 px-5 md:grid-cols-12 md:px-10">
        <div className="flex flex-col justify-between md:col-span-7 md:pb-10">
          <motion.p className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}>
            <span className="mr-3 inline-block h-2 w-2 bg-signal align-middle" />goencho — essentials
          </motion.p>
          <h1 data-testid="hero-title" className="mt-8 font-display text-[3.4rem] font-light leading-[0.92] tracking-tight sm:text-7xl lg:text-[8.5rem]">
            <MaskLines lines={["Quiet", "garments for"]} delay={0.2} />
            <MaskLines lines={["loud minds."]} delay={0.46} lineClass="italic" />
          </h1>
          <motion.div
            className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 1, ease: EASE }}
          >
            <p className="max-w-sm text-base text-stone md:text-lg">
              Heavyweight tees, everyday hoodies and the small things that finish a fit. Fewer pieces, louder ones.
            </p>
            <Link
              to="/shop"
              data-testid="hero-shop-button"
              className="group inline-flex w-fit items-center gap-3 rounded-full bg-ink px-7 py-4 text-sm text-bone transition-colors duration-300 hover:bg-signal"
            >
              Shop the collection
              <ArrowDownRight className="h-4 w-4 transition-transform duration-500 group-hover:-rotate-90" />
            </Link>
          </motion.div>
        </div>
        <div className="md:col-span-5">
          <HeroImage />
        </div>
      </div>
      <motion.div style={{ x: wordX }} aria-hidden="true" className="outline-text mt-10 select-none whitespace-nowrap font-display text-[22vw] italic leading-[0.8] md:-mt-6 md:text-[17vw]">
        goencho goencho
      </motion.div>
    </section>
  );
};
